import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  RefreshCw,
  Sparkles,
  Users,
  CheckCircle2,
  AlertTriangle,
  Zap,
  History,
  Link as LinkIcon,
  ShieldCheck,
  ChevronRight,
  Mail,
  Download,
  Send,
  Ban,
  X,
} from "lucide-react";

import { api } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { renderRich } from "@/lib/renderRich";

export default function ConsultingPerception() {
  const { testId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState(null); // status endpoint
  const [loading, setLoading] = useState(true);
  const [creatingLink, setCreatingLink] = useState(false);
  const [regeneratingLink, setRegeneratingLink] = useState(false);
  const [refreshingSummary, setRefreshingSummary] = useState(false);
  const [generatingContrast, setGeneratingContrast] = useState(false);
  const [summary, setSummary] = useState(null);
  const [classification, setClassification] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedContrast, setSelectedContrast] = useState(null); // full doc
  const [selectedContrastId, setSelectedContrastId] = useState(null);
  const [error, setError] = useState(null);

  const shareUrl = useMemo(() => {
    if (!state?.token) return "";
    return `${window.location.origin}/perception/${state.token}`;
  }, [state?.token]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stateRes = await api.get(`/consulting/${testId}/perception`);
      setState(stateRes.data);
      // Solo intentamos cargar summary/classification si hay token activo
      if (stateRes.data?.enabled) {
        const [sumRes, clsRes, histRes] = await Promise.all([
          api.get(`/consulting/${testId}/perception/summary`).catch(() => ({ data: { exists: false } })),
          api.get(`/consulting/${testId}/perception/classification`).catch(() => ({ data: null })),
          api.get(`/consulting/${testId}/perception/contrast/history`).catch(() => ({ data: { items: [] } })),
        ]);
        setSummary(sumRes.data?.exists ? sumRes.data : null);
        setClassification(clsRes.data?.classification || null);
        setHistory(histRes.data?.items || []);
      } else {
        setSummary(null);
        setClassification(null);
        setHistory([]);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "team_too_small") {
        setError({ kind: "team_too_small", message: d.message, min: d.min_team_size, count: d.employees_count });
      } else if (err?.response?.status === 403) {
        setError({ kind: "forbidden", message: "No tienes acceso a esta consultoría" });
      } else if (err?.response?.status === 404) {
        setError({ kind: "not_found", message: "Consultoría no encontrada" });
      } else {
        setError({ kind: "generic", message: "No pudimos cargar el panel de percepción" });
      }
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function enableLink() {
    setCreatingLink(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/enable`);
      setState(r.data);
      toast.success("Enlace creado. Ya puedes compartirlo con tu equipo.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "team_too_small") {
        setError({ kind: "team_too_small", message: d.message, min: d.min_team_size, count: d.employees_count });
      } else {
        toast.error(d?.message || "No pudimos crear el enlace");
      }
    } finally { setCreatingLink(false); }
  }

  async function regenerateLink() {
    if (!window.confirm(
      "Vas a INVALIDAR el enlace anterior y generar uno nuevo. Los empleados que aún no hayan respondido tendrán que recibir el enlace nuevo.\n\n"
      + "Las respuestas ya recibidas se conservan.\n\n¿Continuar?"
    )) return;
    setRegeneratingLink(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/regenerate`);
      setState(r.data);
      toast.success("Enlace regenerado. El anterior ya no funciona.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No pudimos regenerar el enlace");
    } finally { setRegeneratingLink(false); }
  }

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("No pudimos copiar. Cópialo manualmente.");
    }
  }

  async function refreshSummary() {
    setRefreshingSummary(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/summary/regenerate`);
      setSummary({
        exists: true,
        created_at: r.data.created_at,
        n_responses_at_gen: r.data.n_responses_at_gen,
        content: r.data.content,
      });
      toast.success("Resumen actualizado con las últimas respuestas");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No pudimos actualizar el resumen ahora");
    } finally { setRefreshingSummary(false); }
  }

  async function generateContrast() {
    setGeneratingContrast(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/contrast/generate`);
      toast.success("Contraste generado");
      // recargar historial y abrir el nuevo
      const histRes = await api.get(`/consulting/${testId}/perception/contrast/history`);
      setHistory(histRes.data?.items || []);
      openContrast(r.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No pudimos generar el contraste");
    } finally { setGeneratingContrast(false); }
  }

  async function openContrast(id) {
    setSelectedContrastId(id);
    try {
      const r = await api.get(`/consulting/${testId}/perception/contrast/${id}`);
      setSelectedContrast(r.data);
    } catch {
      toast.error("No pudimos cargar el contraste");
    }
  }

  // ---------- Renderers ----------

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <BrandHeader />
        <div className="mx-auto max-w-6xl px-4 py-16 text-center text-slate-500">Cargando panel de percepción…</div>
      </div>
    );
  }

  if (error?.kind === "team_too_small") {
    return (
      <div className="min-h-screen bg-slate-50">
        <BrandHeader />
        <BackBtn onClick={() => navigate(`/consulting/${testId}/report`)} />
        <main className="mx-auto max-w-3xl px-4 pb-24">
          <div className="rounded-3xl bg-white border border-amber-200 p-8 md:p-10 shadow-sm">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center mb-5">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mb-3">Función bloqueada por privacidad</h1>
            <p className="text-slate-700 leading-relaxed mb-4">{error.message}</p>
            <div className="text-sm text-slate-500 rounded-xl bg-slate-50 p-4">
              Equipo indicado: <strong>{error.count} personas</strong>. Mínimo necesario: <strong>{error.min}</strong>.
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <BrandHeader />
        <BackBtn onClick={() => navigate(`/consulting/${testId}/report`)} />
        <main className="mx-auto max-w-3xl px-4 pb-24 text-center text-slate-500">{error.message}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="perception-panel">
      <BrandHeader />
      <BackBtn onClick={() => navigate(`/consulting/${testId}/report`)} />

      <main className="mx-auto max-w-6xl px-4 pb-24 space-y-6">
        {/* Hero */}
        <section className="rounded-3xl p-8 md:p-10 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>
          <div className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at center, #E5C874 0%, transparent 70%)" }} />
          <div className="relative z-10">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#E5C874" }}>Consultoría Premium · Contraste</div>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold mt-2 leading-tight">Percepciones del equipo</h1>
            <p className="mt-3 text-white/80 max-w-2xl leading-relaxed">Recoge de forma anónima cómo ve tu equipo la gestión del talento y contrástalo con tu propio informe para descubrir puntos ciegos y falsas alarmas.</p>
          </div>
        </section>

        {/* Enlace público */}
        <section className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 space-y-5" data-testid="perception-link-section">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#0F6E56] text-[#E5C874] flex items-center justify-center flex-shrink-0"><LinkIcon className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">Enlace anónimo para tu equipo</h2>
              <p className="text-sm text-slate-600 mt-1">Comparte este enlace con tus empleados. Las respuestas son anónimas y solo agregadas — nunca vemos quién respondió qué.</p>
            </div>
          </div>

          {!state?.enabled ? (
            <button
              onClick={enableLink}
              disabled={creatingLink}
              className="rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
              data-testid="perception-enable-btn"
            >
              {creatingLink ? <><RefreshCw className="w-4 h-4 animate-spin" />Creando…</> : <><Sparkles className="w-4 h-4" />Crear enlace</>}
            </button>
          ) : (
            <>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-xs md:text-sm text-slate-700 break-all leading-relaxed select-all" data-testid="perception-share-url">{shareUrl}</div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={copyLink} className="rounded-full px-4 py-2.5 text-sm font-semibold bg-[#0F6E56] text-white hover:-translate-y-0.5 transition-all inline-flex items-center gap-2" data-testid="perception-copy-btn"><Copy className="w-4 h-4" />Copiar enlace</button>
                <button
                  onClick={regenerateLink}
                  disabled={regeneratingLink}
                  className="rounded-full px-4 py-2.5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-all inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="perception-regen-link-btn"
                  title="Invalidar enlace actual y generar uno nuevo"
                ><RefreshCw className={`w-4 h-4 ${regeneratingLink ? "animate-spin" : ""}`} />Regenerar</button>
                <div className="ml-auto text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" />Enlace activo · {state.regenerated_count > 0 ? `regenerado ${state.regenerated_count} vez(es)` : "original"}</div>
              </div>
            </>
          )}
        </section>

        {/* Progreso */}
        {state?.enabled && <ProgressCard state={state} />}

        {/* Resumen + Contraste */}
        {state?.enabled && state?.threshold_reached && (
          <>
            <SummaryCard summary={summary} onRefresh={refreshSummary} refreshing={refreshingSummary} atMin={state.threshold?.at_threshold_minimum} />
            <ContrastActionCard
              canGenerate={!!summary?.exists}
              onGenerate={generateContrast}
              generating={generatingContrast}
              classification={classification}
            />
          </>
        )}

        {/* Historial */}
        {state?.enabled && history.length > 0 && (
          <HistoryCard history={history} selectedId={selectedContrastId} onOpen={openContrast} />
        )}

        {/* Contraste seleccionado */}
        {selectedContrast && <ContrastDetail contrast={selectedContrast} testId={testId} onClose={() => { setSelectedContrast(null); setSelectedContrastId(null); }} />}
      </main>
    </div>
  );
}

// ---------- Sub-components ----------

function BackBtn({ onClick }) {
  return (
    <div className="mx-auto max-w-6xl px-4 pt-6">
      <button onClick={onClick} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors" data-testid="perception-back-btn">
        <ArrowLeft className="w-4 h-4" />Volver al informe
      </button>
    </div>
  );
}

function ProgressCard({ state }) {
  const { n_responses, threshold, threshold_reached, employees_count } = state;
  const required = threshold?.required || 3;
  const pct = Math.min(100, Math.round((n_responses / required) * 100));
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="perception-progress">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${threshold_reached ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {threshold_reached ? <CheckCircle2 className="w-5 h-5" /> : <Users className="w-5 h-5" />}
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Progreso</h2>
            <p className="text-sm text-slate-600 mt-0.5">{n_responses} de {required} respuestas necesarias · equipo indicado: {employees_count}</p>
          </div>
        </div>
        {threshold_reached ? (
          <span className="badge bg-emerald-100 text-emerald-700 font-semibold">Umbral alcanzado</span>
        ) : (
          <span className="badge bg-amber-100 text-amber-700 font-semibold">Faltan {Math.max(0, required - n_responses)} para el umbral</span>
        )}
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: threshold_reached ? "linear-gradient(90deg, #0F6E56, #C9A84C)" : "#C9A84C" }} />
      </div>
      {threshold?.at_threshold_minimum && threshold_reached && (
        <p className="mt-3 text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />Estás justo en el mínimo. Con más respuestas el resumen será más representativo.</p>
      )}
    </section>
  );
}

function SummaryCard({ summary, onRefresh, refreshing, atMin }) {
  const has = summary?.exists;
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="perception-summary-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#0F6E56] text-[#E5C874] flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">Resumen conjunto del equipo</h2>
            <p className="text-sm text-slate-600 mt-0.5">Fotografía anónima generada por IA a partir de las respuestas abiertas.</p>
          </div>
        </div>
        <button onClick={onRefresh} disabled={refreshing} className="rounded-full px-4 py-2.5 text-sm font-semibold border border-[#0F6E56]/25 text-[#0F6E56] hover:bg-[#0F6E56]/5 transition-all inline-flex items-center gap-2 disabled:opacity-60" data-testid="perception-refresh-summary-btn">
          {refreshing ? <><RefreshCw className="w-4 h-4 animate-spin" />Actualizando…</> : <><Zap className="w-4 h-4" />Actualizar ahora</>}
        </button>
      </div>
      {!has ? (
        <div className="text-sm text-slate-500 italic">Aún no hay resumen. Pulsa <em>Actualizar ahora</em> para generar el primero, o espera al ciclo automático (cada 12 h).</div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed" data-testid="perception-summary-global">{renderRich(summary.content.global_summary)}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {["A","B","C","D","E","F"].map(code => (
              <AreaSummaryChip key={code} code={code} text={summary.content.by_area[code]} />
            ))}
          </div>
          {summary.content.global_closing && (
            <div className="rounded-xl bg-gradient-to-br from-[#0F6E56]/5 to-[#C9A84C]/5 border border-[#0F6E56]/10 p-4 text-sm text-slate-700 italic" data-testid="perception-summary-closing">
              <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#0F6E56] block mb-1">Mensajes de cierre</span>
              {renderRich(summary.content.global_closing)}
            </div>
          )}
          <div className="text-xs text-slate-400">Basado en {summary.n_responses_at_gen} respuestas · generado {formatDate(summary.created_at)}{atMin ? " · muestra en el mínimo" : ""}</div>
        </div>
      )}
    </section>
  );
}

const AREA_NAMES = {
  A: "Marca empleadora", B: "Selección", C: "Onboarding",
  D: "Desempeño", E: "Feedback", F: "Desarrollo",
};

function AreaSummaryChip({ code, text }) {
  const empty = !text || /^No hay comentarios/i.test(text);
  return (
    <div className={`rounded-xl border p-3 text-xs leading-relaxed ${empty ? "bg-slate-50 border-slate-200 text-slate-400 italic" : "bg-white border-[#0F6E56]/15 text-slate-700"}`}>
      <div className={`text-[10px] font-semibold tracking-[0.14em] uppercase mb-1 ${empty ? "text-slate-400" : "text-[#0F6E56]"}`}>[{code}] {AREA_NAMES[code]}</div>
      {renderRich(text)}
    </div>
  );
}

function ContrastActionCard({ canGenerate, onGenerate, generating, classification }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="perception-contrast-action">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C9A84C] to-[#E5C874] text-[#0b3d2e] flex items-center justify-center flex-shrink-0"><Zap className="w-5 h-5" /></div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold text-slate-900">Contraste con tu percepción</h2>
          <p className="text-sm text-slate-600 mt-0.5">Compara lo que respondió tu equipo con lo que respondiste tú en el informe de consultoría.</p>
        </div>
      </div>
      {classification?.focus_areas?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Foco recomendado:</span>
          {classification.focus_areas.map(c => (
            <span key={c} className="rounded-full px-3 py-1 font-semibold text-white" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>[{c}] {AREA_NAMES[c]}</span>
          ))}
        </div>
      )}
      <button
        onClick={onGenerate}
        disabled={!canGenerate || generating}
        className="rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
        style={{ background: canGenerate ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" : "#94a3b8" }}
        data-testid="perception-generate-contrast-btn"
        title={!canGenerate ? "Necesitas generar el resumen primero" : ""}
      >
        {generating ? <><RefreshCw className="w-4 h-4 animate-spin" />Generando contraste…</> : <><Sparkles className="w-4 h-4" />Generar contraste ahora</>}
      </button>
      {!canGenerate && <p className="mt-3 text-xs text-slate-500 italic">Genera antes el resumen del equipo (botón "Actualizar ahora").</p>}
    </section>
  );
}

function HistoryCard({ history, selectedId, onOpen }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="perception-history">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><History className="w-4 h-4" /></div>
        <h2 className="font-display text-lg font-semibold text-slate-900">Historial de contrastes</h2>
        <span className="text-xs text-slate-400">({history.length})</span>
      </div>
      <div className="space-y-2">
        {history.map(item => (
          <button
            key={item.id}
            onClick={() => onOpen(item.id)}
            className={`w-full text-left rounded-xl p-4 border transition-all ${selectedId === item.id ? "border-[#0F6E56] bg-[#0F6E56]/5" : "border-slate-200 hover:border-[#0F6E56]/40 hover:bg-slate-50"}`}
            data-testid={`perception-history-item-${item.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 font-medium truncate">{renderRich(item.headline)}</div>
                <div className="text-xs text-slate-500 mt-1">{formatDate(item.created_at)} · {item.n_responses_at_gen} respuestas</div>
                {item.focus_areas?.length > 0 && (
                  <div className="mt-2 flex gap-1.5 flex-wrap">
                    {item.focus_areas.map(c => (
                      <span key={c} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{c}</span>
                    ))}
                  </div>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function ContrastDetail({ contrast, testId, onClose }) {
  const c = contrast.content;
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [sending, setSending] = useState(false);
  const [preparingDl, setPreparingDl] = useState(false);
  const [emailForm, setEmailForm] = useState({ open: false, to: "" });
  const [revokingId, setRevokingId] = useState(null);

  const loadShares = useCallback(async () => {
    setLoadingShares(true);
    try {
      const r = await api.get(`/consulting/${testId}/perception/contrast/${contrast.id}/shares`);
      setShares(r.data?.items || []);
    } catch {
      // silencioso — es panel secundario
    } finally { setLoadingShares(false); }
  }, [testId, contrast.id]);

  useEffect(() => { loadShares(); }, [loadShares]);

  async function handleSendEmail(e) {
    e?.preventDefault?.();
    const to = emailForm.to.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Introduce un email válido");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/contrast/${contrast.id}/send-email`, { to_email: to });
      const expires = r.data?.share?.expires_at;
      const label = expires ? formatDate(expires) : "en 15 días";
      if (r.data?.email_sent) {
        toast.success(`Enviado a ${to}. El enlace caduca el ${label}.`, { duration: 6000 });
      } else {
        toast.warning(`Link generado pero Resend falló. Copia manualmente el link: ${r.data?.download_url}`, { duration: 12000 });
      }
      setEmailForm({ open: false, to: "" });
      loadShares();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No pudimos enviar el email");
    } finally { setSending(false); }
  }

  async function handleDownloadNow() {
    setPreparingDl(true);
    try {
      const r = await api.post(`/consulting/${testId}/perception/contrast/${contrast.id}/prepare-download`);
      const url = r.data?.download_url;
      if (!url) throw new Error("no url");
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("PDF listo — abriendo en nueva pestaña");
      loadShares();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "No pudimos preparar el PDF");
    } finally { setPreparingDl(false); }
  }

  async function handleRevoke(shareId, sentTo) {
    if (!window.confirm(
      sentTo
        ? `¿Revocar el enlace enviado a ${sentTo}? Dejará de funcionar inmediatamente.`
        : "¿Revocar este enlace? Dejará de funcionar inmediatamente."
    )) return;
    setRevokingId(shareId);
    try {
      await api.post(`/consulting/${testId}/perception/shares/${shareId}/revoke`);
      toast.success("Enlace revocado. Ya no funciona.");
      loadShares();
    } catch (err) {
      toast.error("No pudimos revocar el enlace");
    } finally { setRevokingId(null); }
  }

  async function copyShareLink(token) {
    const url = `${window.location.origin}/api/perception/download/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No pudimos copiar");
    }
  }

  return (
    <section className="space-y-4" data-testid="perception-contrast-detail">
      <div className="rounded-3xl p-8 md:p-10 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white text-sm" data-testid="perception-contrast-close-btn">✕ Cerrar</button>
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#E5C874" }}>Contraste generado el {formatDate(contrast.created_at)}</div>
        <h2 className="font-display text-2xl md:text-3xl font-semibold mt-2 leading-tight">{renderRich(c.headline)}</h2>
        <div className="mt-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-4 text-white/90 text-sm leading-relaxed">{renderRich(c.executive)}</div>
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {["A","B","C","D","E","F"].map(code => {
            const cls = contrast.classification?.by_area?.[code];
            const badge = cls?.kind === "coincidencia" ? { txt: "Coincidencia", color: "bg-emerald-500/25 text-emerald-100 border-emerald-400/30" }
              : cls?.kind === "punto_ciego" ? { txt: "Punto ciego", color: "bg-red-500/25 text-red-100 border-red-400/30" }
              : cls?.kind === "falsa_alarma" ? { txt: "Falsa alarma", color: "bg-amber-500/25 text-amber-100 border-amber-400/30" }
              : { txt: "Sin datos", color: "bg-slate-500/25 text-slate-200 border-slate-400/30" };
            return (
              <div key={code} className="rounded-xl bg-white/8 border border-white/15 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold tracking-[0.14em] uppercase" style={{ color: "#E5C874" }}>[{code}] {AREA_NAMES[code]}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.color}`}>{badge.txt}</span>
                  {cls?.distance > 0 && <span className="text-[10px] text-white/60">·  d={cls.distance}</span>}
                </div>
                <p className="text-sm text-white/85 leading-relaxed">{renderRich(c.by_area[code])}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-xl bg-gradient-to-br from-[#C9A84C]/25 to-[#E5C874]/15 border border-[#C9A84C]/30 p-5">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase mb-2" style={{ color: "#E5C874" }}>Foco recomendado</div>
          <p className="text-sm text-white/95 leading-relaxed">{renderRich(c.recommended_focus)}</p>
        </div>
      </div>

      {/* Acciones PDF */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8" data-testid="perception-contrast-actions">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-11 h-11 rounded-xl bg-[#0F6E56] text-[#E5C874] flex items-center justify-center flex-shrink-0"><Download className="w-5 h-5" /></div>
          <div className="flex-1">
            <h3 className="font-display text-lg md:text-xl font-semibold text-slate-900">Compartir por PDF</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              El PDF se genera una sola vez y se sirve desde un enlace temporal (15 días). Puedes revocarlo en cualquier momento.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDownloadNow}
            disabled={preparingDl}
            className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2"
            style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
            data-testid="perception-download-now-btn"
          >
            {preparingDl ? <><RefreshCw className="w-4 h-4 animate-spin" />Preparando PDF…</> : <><Download className="w-4 h-4" />Descargar PDF ahora</>}
          </button>
          <button
            onClick={() => setEmailForm({ open: true, to: "" })}
            className="rounded-full px-5 py-2.5 text-sm font-semibold border border-[#0F6E56]/30 text-[#0F6E56] hover:bg-[#0F6E56]/5 transition-all inline-flex items-center gap-2"
            data-testid="perception-open-email-form-btn"
          >
            <Mail className="w-4 h-4" />Enviar por email
          </button>
        </div>

        {emailForm.open && (
          <form onSubmit={handleSendEmail} className="mt-5 rounded-xl border border-[#0F6E56]/20 bg-[#0F6E56]/5 p-4 space-y-3" data-testid="perception-email-form">
            <div className="text-xs font-semibold text-[#0F6E56] uppercase tracking-[0.16em]">Enviar link temporal por email</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                required
                autoFocus
                placeholder="cofundador@empresa.com"
                value={emailForm.to}
                onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/30 focus:border-[#0F6E56]"
                data-testid="perception-email-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 inline-flex items-center gap-2 justify-center"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
                data-testid="perception-send-email-btn"
              >
                {sending ? <><RefreshCw className="w-4 h-4 animate-spin" />Enviando…</> : <><Send className="w-4 h-4" />Enviar</>}
              </button>
              <button
                type="button"
                onClick={() => setEmailForm({ open: false, to: "" })}
                disabled={sending}
                className="rounded-full px-3 py-2.5 text-sm text-slate-500 hover:text-slate-800"
                data-testid="perception-email-form-cancel-btn"
                title="Cancelar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              El destinatario recibirá un enlace válido 15 días. El link es de posesión — puede reenviarlo a otras personas, pero tú puedes revocarlo desde el historial de abajo.
            </p>
          </form>
        )}

        {/* Historial de envíos / descargas */}
        <div className="mt-6" data-testid="perception-shares-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-[0.16em]">Historial de enlaces</div>
            <button
              onClick={loadShares}
              className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
              data-testid="perception-shares-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loadingShares ? "animate-spin" : ""}`} />Actualizar
            </button>
          </div>
          {loadingShares ? (
            <div className="text-xs text-slate-400 italic">Cargando historial…</div>
          ) : shares.length === 0 ? (
            <div className="text-xs text-slate-400 italic">Aún no se ha generado ningún enlace de descarga.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {shares.map((s) => {
                const isRevoked = s.revoked;
                const isExpired = !isRevoked && s.expires_at && new Date(s.expires_at) < new Date();
                const status = isRevoked ? { txt: "REVOCADO", cls: "bg-red-100 text-red-700" }
                  : isExpired ? { txt: "EXPIRADO", cls: "bg-slate-200 text-slate-600" }
                  : { txt: "ACTIVO", cls: "bg-emerald-100 text-emerald-700" };
                return (
                  <li key={s.id} className="py-3 flex items-start justify-between gap-3" data-testid={`perception-share-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${status.cls}`}>{status.txt}</span>
                        <span className="text-xs text-slate-500">{s.purpose === "email" ? "Email" : "Descarga propia"}</span>
                        {s.sent_to && <span className="text-xs text-slate-800 font-medium truncate">→ {s.sent_to}</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        Creado {formatDate(s.created_at)} · caduca {formatDate(s.expires_at)}
                        {s.accessed_count > 0 && ` · abierto ${s.accessed_count}×`}
                        {isRevoked && s.revoked_at && ` · revocado ${formatDate(s.revoked_at)}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isRevoked && !isExpired && (
                        <>
                          <button
                            onClick={() => copyShareLink(s.token)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                            title="Copiar enlace"
                            data-testid={`perception-share-${s.id}-copy-btn`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleRevoke(s.id, s.sent_to)}
                            disabled={revokingId === s.id}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-700 disabled:opacity-40"
                            title="Revocar enlace"
                            data-testid={`perception-share-${s.id}-revoke-btn`}
                          >
                            {revokingId === s.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
